"use client";

import { useWindowWidth } from "@react-hook/window-size";
import moment from "moment";
import { ReactNode, useEffect, useRef, useState } from "react";

import CurrencyDisplay from "@/app/components/currencyDisplay";
import NumberOptionList from "@/app/components/numberOptionList";
import Preloader from "@/app/components/preloader";
import { ProductAvailabilityState } from "@/app/ec/product/[basePath]/page";
import { OrderHistoryResponse } from "@/app/ec/product/[basePath]/purchase-history";
import getProductDetailsFromClient from "@/app/ec/product/[basePath]/getProductDetailsFromClient";
import StockAndShippingMessage from "@/app/ec/product/[basePath]/stockAndShippingMessage";
import VendorPicker from "@/app/ec/product/[basePath]/vendor-options/vendorPicker";

import getChosenPriceBreakExpiration from "@/app/ec/product/[basePath]/vendor-options/getChosenPriceBreakExpiration";
import getVendorOptions from "@/app/ec/product/[basePath]/vendor-options/getVendorOptions";
import { useProductForSaleStatusNeo } from "@/app/hooks/useProductForSaleStatus";
import Badge from "@/components/design-system/badge";
import Button from "@/components/design-system/button";
import Dropdown from "@/components/design-system/dropdown";
import Modal from "@/components/design-system/modal";
import QuantityPicker from "@/components/design-system/quantity-picker";
import Tooltip from "@/components/design-system/tooltip";
import LicenseRequiredBadge from "@/components/license-required-badge/licenseRequiredBadge";
import { useShowLicenseRequiredBadge } from "@/app/hooks/useLicenseRequiredBadge";
import { licenseIsRequired, productDetailsRequireLicense } from "@/utils/licenseRequired";
import { useModifyShoppingCartMutation } from "@/store/shoppingCartApi";

import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import InfoIcon from "public/next-public/common/icons/info-icon.svg";
import StarIcon from "public/next-public/common/icons/star-solid-icon.svg";
import WarningIcon from "public/next-public/common/icons/warning-icon.svg";

import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";
import style from "./purchaseHistory.module.scss";

const BOOTSTRAP_MD_BREAKPOINT = 768;

export type PurchaseHistoryModalProps = {
  mpId: number;
  isVisible: boolean;
  closeCallback: () => void;
  /** Null while the lazy fetch is in flight — the modal body shows its preloader. */
  purchaseHistory: OrderHistoryResponse | null;
  /** Terminal fetch failure — renders an error message instead of the preloader. */
  loadFailed?: boolean;
  categoriesTree: CategoryTree[];
  purchaseHistoryCallback?: (mpId: string | number, vpId: string | number, quantity: number) => void;
  quantity?: number;
  userDeliveryZip?: string;
  /** Replaces the stock and handling-time line under the vendor name. */
  renderStockLine?: (vendorOption: VendorOptionState) => ReactNode;
  /** Replaces the shipping cost next to the price. */
  renderShippingLine?: (vendorOption: VendorOptionState) => ReactNode;
};

export const PurchaseHistoryModal = ({
  mpId,
  isVisible,
  closeCallback,
  purchaseHistory,
  categoriesTree,
  loadFailed = false,
  purchaseHistoryCallback,
  userDeliveryZip,
  renderStockLine,
  renderShippingLine,
}: PurchaseHistoryModalProps) => {
  const router = useRouter();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < BOOTSTRAP_MD_BREAKPOINT;
  const [awaitingServer, setAwaitingServer] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [showProductForm, setShowProductForm] = useState(true);
  const [chosenVendorKey, setChosenVendorKey] = useState<number>(0);
  const [vendorOptions, setVendorOptions] = useState<VendorOptionState[]>([]);
  const [maxInventoryAvailable, setMaxInventoryAvailable] = useState<number>(1);
  const [vendorOptionsRetrievedAndConfigured, setVendorOptionsRetrievedAndConfigured] = useState(false);
  const [vendorOptionsIncludingOutOfStock, setVendorOptionsIncludingOutOfStock] = useState<VendorOptionFromServer[]>(
    [],
  );
  const [vendorProductId, setVendorProductId] = useState(0);
  const [vendorPickerVisible, setVendorPickerVisible] = useState(false);
  const [manufacturerOrCategoryLicenseIsRequired, setManufacturerOrCategoryLicenseIsRequired] = useState(false);
  const vendorPickerTarget = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    configureVendors(selectedQuantity, true, false);
  }, [isVisible]);

  // A license requirement set at the manufacturer or category level never reaches the
  // vendor options, so it has to come from the PDP payload. Fetched here rather than
  // passed in, so every entry point to this modal behaves the same.
  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    getProductDetailsFromClient(mpId).then((details) => {
      if (!cancelled) {
        setManufacturerOrCategoryLicenseIsRequired(productDetailsRequireLicense(details));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isVisible, mpId]);

  // For-sale status comes from the live vendor options this modal fetches itself
  // (configureVendors) — NOT from the history payload: the modern orders endpoint always
  // returns an empty vendorOptions array (buy-box data is deliberately not part of it).
  const { availabeToPurchase, outOfStock } = useProductForSaleStatusNeo({
    vendorProducts: vendorOptionsIncludingOutOfStock,
  });

  const availabilityState = ((): ProductAvailabilityState => {
    if (!purchaseHistory || vendorOptionsRetrievedAndConfigured === false || typeof vendorOptions === "undefined")
      return "pending";
    else if (
      (vendorOptionsRetrievedAndConfigured === true && !availabeToPurchase) ||
      (availabeToPurchase &&
        vendorOptions.length === 0 &&
        vendorOptionsIncludingOutOfStock &&
        vendorOptionsIncludingOutOfStock.length === 0)
    ) {
      return "not-for-sale";
    } else if (outOfStock) return "out-of-stock";
    else return "for-sale";
  })();

  const closeModal = () => {
    setChosenVendorKey(0);
    setSelectedQuantity(0);
    setAwaitingServer(false);
    closeCallback();
  };

  const changeVendorHandler = (vendor: number) => {
    setChosenVendorKey(vendor);
    setMaxInventoryAvailable(vendorOptions[vendor].inventory);
  };

  const getStatus = (order: ProductHistoryProducts) => {
    if (order.quantity === order.totalShipped) {
      return "Shipped";
    } else {
      if (order.totalCancelled > 0) {
        if (order.totalReturned > 0) {
          const netCancelled = order.totalCancelled - order.totalReturned;
          if (order.totalCancelled >= netCancelled) return "Returned";
          else return "Cancelled";
        } else if (order.quantity === order.totalCancelled) {
          return "Cancelled";
        }
      }
      // the lack of else here is intentional. This should run if one of the above returns doesn't happen
      if (order.totalBackordered > 0) return "Backordered";
      if (order.quantity === order.totalPending) return "Pending";
      // This generally means items are cancelled but some of it has shipped.  Since the entire order isn't cancelled, but stuff
      // has been shipped, we'll mark it as shipped.
      if (order.totalShipped > 0 && order.totalPending === 0) return "Shipped";
      return "Unknown";
    }
  };

  const configureVendors = async (
    quantity: number,
    doInitializeSelectedQuantity: boolean,
    resetChosenVendorKey: boolean,
  ) => {
    const zipCode = Cookies.get("n32UserPrimaryZip");
    const vendorOptionState = await getVendorOptions(mpId, quantity, zipCode);
    const vendorIndex = resetChosenVendorKey ? 0 : chosenVendorKey;
    //  chosenVendorKey may not be updated yet due to react setState asynchronous assignment
    // Only trust the arguments and local variables to tell us the true state
    setVendorOptionsIncludingOutOfStock(vendorOptionState);
    setVendorOptions(vendorOptionState);
    setVendorOptionsRetrievedAndConfigured(true);
    if (vendorOptionState.length === 0) {
      setMaxInventoryAvailable(0);
      return;
    }
    setMaxInventoryAvailable(vendorOptionState[vendorIndex].inventory);

    if (doInitializeSelectedQuantity) {
      setSelectedQuantity(vendorOptionState[vendorIndex].chosenPriceBreakMinQty);
    }
  };

  const updateQuantity = async (newQuantity: number) => {
    setShowProductForm(false);
    try {
      setSelectedQuantity(newQuantity);
      setChosenVendorKey(0);

      configureVendors(newQuantity, false, true);
    } catch (error) {
      console.error("getVendorOptions error occurred: ", error);
    }
    setShowProductForm(true);
  };

  const [modifyShoppingCart, modifyShoppingCartMutationState] = useModifyShoppingCartMutation();

  const addToCart = (vpId: number) => {
    const categoryName = categoriesTree.length > 0 ? categoriesTree[categoriesTree.length - 1].name : "";
    if (!vpId || vpId <= 0 || !categoryName) {
      // Cart contract requires a valid vendor product and leaf category — never submit without them.
      console.error("Add to cart blocked: missing vendor product or category", { vpId, categoryName });
      return;
    }
    setVendorProductId(vpId);
    setAwaitingServer(true);
    const productToAdd: ModifyCartProduct = {
      mpId: mpId,
      vendorProductId: vpId,
      quantity: selectedQuantity,
      minimumQuantity: selectedQuantity,
      categoryName: categoryName,
    };
    modifyShoppingCart({
      productsToModify: [productToAdd],
      incrementQuantity: false,
    });

    if (purchaseHistoryCallback) {
      purchaseHistoryCallback(mpId, vpId, 1);
    }
  };

  useEffect(() => {
    if (modifyShoppingCartMutationState.isSuccess) {
      router.push(`/shopping-cart-acknowledgement/${mpId}/${vendorProductId}`);
    } else if (modifyShoppingCartMutationState.error) {
      setAwaitingServer(false);
      alert("An unknown error occurred.  Please try again.");
    }
  }, [modifyShoppingCartMutationState, mpId, vendorProductId]);

  const setCookieAndFetchOptions = (elementId: string, cookieName: string, mpId: number, quantity: number) => {
    const element = document.getElementById(elementId);
    if (element) {
      const value = element.innerHTML;
      const existingCookie = Cookies.get(cookieName);

      if (!existingCookie || existingCookie !== value) {
        Cookies.set(cookieName, value, { expires: 1 / 24 });
        getVendorOptions(mpId, quantity, userDeliveryZip);
      }

      return setInterval(() => {
        const validCookie = Cookies.get(cookieName);
        if (!validCookie) {
          Cookies.set(cookieName, value, { expires: 1 / 24 });
          getVendorOptions(mpId, quantity, userDeliveryZip);
        }
      }, 1000 * 60);
    }
  };

  useEffect(() => {
    const handleFactorIDChange = () =>
      setCookieAndFetchOptions("factorIdDiv", "buyBoxFactorIdExperiment", mpId, selectedQuantity);
    const handleAuthorizedBoostedChange = () =>
      setCookieAndFetchOptions("applyAuthorizedBoostedDiv", "applyAuthorizedBoostedExperiment", mpId, selectedQuantity);

    window.addEventListener("factorIDChange", handleFactorIDChange);
    window.addEventListener("applyAuthorizedBoostedChange", handleAuthorizedBoostedChange);

    // Cleanup event listeners and intervals on component unmount
    return () => {
      window.removeEventListener("factorIDChange", handleFactorIDChange);
      window.removeEventListener("applyAuthorizedBoostedChange", handleAuthorizedBoostedChange);
    };
  }, []);

  const chosenVendorOption = vendorOptions[chosenVendorKey];
  const isLicenseRequired = licenseIsRequired(chosenVendorOption, manufacturerOrCategoryLicenseIsRequired);
  // Derived above the `isVisible` early return so the hook runs on every render.
  const showLicenseRequiredBadge = useShowLicenseRequiredBadge(isLicenseRequired);

  if (isVisible === false) {
    return null;
  }

  const minPurchasableQuantity = chosenVendorOption?.minimumQuantityPurchasable ?? 1;

  const chosenPriceBreakExpiration = getChosenPriceBreakExpiration(chosenVendorOption);

  const stylizedPrice = (() => {
    if (!chosenVendorOption) return null;
    const [dollars, cents] = chosenVendorOption.chosenPriceBreakUnitPrice.toFixed(2).split(".");
    return (
      <span className={style.stylizedPrice}>
        <sup className={`${style.priceSup} ${style.priceSupDollar}`}>$</sup>
        <span className={style.priceDollars}>{Number(dollars).toLocaleString("en-US")}</span>
        <sup className={style.priceSup}>{cents}</sup>
      </span>
    );
  })();

  const shippingLine = (() => {
    if (!chosenVendorOption) return null;
    if (!chosenVendorOption.standardShipping || Number(chosenVendorOption.standardShipping) <= 0) {
      return (
        <span className={style.shippingText}>
          {"& "}
          <span className={style.freeShipping}>FREE</span> Standard Shipping
        </span>
      );
    }
    return (
      <span className={style.shippingText}>
        {"+ "}
        <strong>
          <CurrencyDisplay value={chosenVendorOption.standardShipping.toString()} />
        </strong>{" "}
        Standard Shipping
        <Tooltip
          tailPlacement="above"
          text="This is a flat shipping fee from this seller, regardless of the quantity you buy."
        >
          <button type="button" className={style.shippingInfoButton} aria-label="Shipping cost details">
            <InfoIcon width={16} height={16} aria-hidden />
          </button>
        </Tooltip>
      </span>
    );
  })();

  return (
    <>
      <div id="factorIdDiv" style={{ display: "none" }}></div> {/*This is for the multi-factor buy box id experiment*/}
      <div id="applyAuthorizedBoostedDiv" style={{ display: "none" }}></div>{" "}
      {/*This is for the multi-factor buy box id experiment*/}
      <Modal
        show={true}
        size={isMobile ? "full" : "medium"}
        onHide={closeModal}
        title="Product Purchase History"
        className={style.PurchaseHistoryModal}
        footer={
          purchaseHistory != null && purchaseHistory?.manufacturerProduct != null ? (
            <div className={style.footerRow}>
              {showProductForm && availabeToPurchase && availabilityState === "for-sale" && chosenVendorOption ? (
                <>
                  <div className={style.vendorColumn} id="vendor-picker-dropdown">
                    <div className={style.vendorRatingsRow}>
                      {vendorOptions.length > 1 ? (
                        <button
                          type="button"
                          ref={vendorPickerTarget}
                          className={style.vendorPickerTrigger}
                          onClick={() => setVendorPickerVisible((visible) => !visible)}
                          aria-haspopup="listbox"
                          aria-expanded={vendorPickerVisible}
                        >
                          <span className={style.vendorName}>{chosenVendorOption.vendorName}</span>
                          <CaretDownIcon width={12} height={12} aria-hidden />
                        </button>
                      ) : (
                        <span className={style.vendorName}>{chosenVendorOption.vendorName}</span>
                      )}
                      {chosenVendorOption.vdrNumberOfGeneralRatings > 0 && (
                        <span className={style.vendorStars}>
                          <StarIcon className={style.starIcon} width={16} height={16} aria-hidden />
                          <strong className={style.ratingValue}>{chosenVendorOption.vendorRating.toFixed(1)}</strong>
                          <span className={style.ratingCount}>({chosenVendorOption.vdrNumberOfGeneralRatings})</span>
                        </span>
                      )}
                    </div>
                    {vendorOptions.length > 1 && (
                      <VendorPicker
                        vendorOptions={vendorOptions}
                        quantity={selectedQuantity}
                        chosenVendorKey={chosenVendorKey}
                        visible={vendorPickerVisible}
                        hideCallback={() => setVendorPickerVisible(false)}
                        targetRef={vendorPickerTarget}
                        setChosenVendorKey={changeVendorHandler}
                      />
                    )}
                    {renderStockLine ? (
                      <span className={style.stockLine}>{renderStockLine(chosenVendorOption)}</span>
                    ) : (
                      <StockAndShippingMessage
                        vendorOption={chosenVendorOption}
                        className={style.stockLine}
                        stockMessageClassName={style.stockedText}
                        totalVendors={vendorOptions.length}
                      />
                    )}
                    {(chosenVendorOption.isAuthorizedDistributor ||
                      chosenPriceBreakExpiration ||
                      showLicenseRequiredBadge) && (
                      <div className={style.badgesRow}>
                        {chosenVendorOption.isAuthorizedDistributor && <AuthorizedDistributorBadge />}
                        {chosenPriceBreakExpiration && (
                          <Badge
                            label={`Expires ${moment(chosenPriceBreakExpiration, "MM/YYYY").format("MMM YYYY")}`}
                            color="red"
                            size="small"
                            icon={<WarningIcon width={12} height={12} aria-hidden />}
                          />
                        )}
                        <LicenseRequiredBadge licenseIsRequired={isLicenseRequired} />
                      </div>
                    )}
                  </div>
                  <div className={style.buyColumn}>
                    <div className={style.priceAndShippingRow}>
                      {stylizedPrice}
                      {renderShippingLine ? (
                        <span className={style.shippingText}>{renderShippingLine(chosenVendorOption)}</span>
                      ) : (
                        shippingLine
                      )}
                    </div>
                    <div className={style.quantityAndCart}>
                      <QuantityPicker
                        className={style.quantityPicker}
                        value={selectedQuantity}
                        min={minPurchasableQuantity}
                        max={maxInventoryAvailable}
                        disabled={awaitingServer}
                        onChange={(next) => {
                          updateQuantity(next);
                        }}
                      />
                      <div className={style.quantityDropdown}>
                        <Dropdown
                          size="compact"
                          ariaLabel="Quantity"
                          value={selectedQuantity.toString()}
                          disabled={awaitingServer}
                          onChange={(event) => {
                            updateQuantity(Number.parseInt(event.target.value));
                          }}
                        >
                          <NumberOptionList start={minPurchasableQuantity} end={maxInventoryAvailable} />
                        </Dropdown>
                      </div>
                      <div className={style.cartButtonWrap}>
                        <Button
                          variant="primary"
                          color="teal"
                          size="large"
                          text="Add to Cart"
                          showRightIcon={false}
                          loading={awaitingServer}
                          onClick={() => addToCart(chosenVendorOption.vendorProductId)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={style.preLoaderProductContainer}>
                  <Preloader />
                  <span>Calculating Best Price</span>
                </div>
              )}
            </div>
          ) : undefined
        }
      >
        <div className={style.body}>
          {purchaseHistory != null && purchaseHistory?.productHistoryProducts != null ? (
            <div className={style.contentContainer}>
              <div className={style.itemDetails}>
                <div className={style.itemRow}>
                  <div className={style.itemLabel}>Item:</div>
                  <div className={style.itemValue}>{purchaseHistory?.manufacturerProduct?.defDescr}</div>
                </div>
                <div className={style.itemRow}>
                  <div className={style.itemLabel}>Manufacturer:</div>
                  <div className={style.itemValue}>{purchaseHistory?.manufacturerProduct?.manName}</div>
                </div>
                <div className={style.itemRow}>
                  <div className={style.itemLabel}>Brand:</div>
                  <div className={style.itemValue}>{purchaseHistory?.manufacturerProduct?.brandName}</div>
                </div>
              </div>
              <div className={style.purchasesSection}>
                <div className={style.totalProductPurchases}>
                  {purchaseHistory?.productHistoryProducts?.length} total{" "}
                  {purchaseHistory?.productHistoryProducts?.length === 1 ? "purchase" : "purchases"}
                </div>
                {purchaseHistory?.productHistoryProducts?.length > 0 && (
                  <div className={style.historyTable}>
                    <div className={style.tableHead}>
                      <div className={style.headerRow}>
                        <div className={style.vendorCol}>Vendor</div>
                        <div className={style.unitPriceCol}>Unit Price</div>
                        <div className={`${style.quantityCol} ${style.desktopOnly}`}>Quantity</div>
                        <div className={style.orderDateCol}>Order Date</div>
                        <div className={`${style.statusCol} ${style.desktopOnly}`}>Status</div>
                      </div>
                      <div className={style.headerDivider} />
                    </div>
                    <div className={style.tableRows}>
                      {purchaseHistory?.productHistoryProducts.map((options: ProductHistoryProducts, i: number) => {
                        return (
                          <div className={style.lineItem} key={i}>
                            <div className={style.vendorCol}>
                              <a href={"/vendor/" + options.vendorId} className={style.highlightedLink}>
                                {options.vendorName}
                              </a>
                            </div>
                            <div className={style.unitPriceCol}>
                              <CurrencyDisplay value={options.productPrice} />
                            </div>
                            <div className={`${style.quantityCol} ${style.desktopOnly}`}>{options.quantity}</div>
                            <div className={style.orderDateCol}>
                              <a
                                href={"/account/orders/details/" + options.customerOrderId}
                                className={style.highlightedLink}
                              >
                                {moment(options.vendorOrderTime).format("MM/DD/YYYY")}
                              </a>
                            </div>
                            <div className={`${style.statusCol} ${style.desktopOnly}`}>{getStatus(options)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : loadFailed ? (
            <div className={style.preLoader}>
              Failed to load purchase history. This may be due to an internal server error — please close and try again.
            </div>
          ) : (
            <div className={style.preLoader}>
              <Preloader />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export type ProductHistoryProducts = {
  customerOrderId: number;
  vendorOrderTime: string;
  vendorId: number;
  vendorName: string;
  mpId: number;
  quantity: number;
  description: string;
  brandName: string;
  totalShipped: number;
  totalCancelled: number;
  totalBackordered: number;
  totalReturned: number;
  totalPending: number;
  productPrice: number;
  productRetailPrice: number;
  forSale: boolean;
};
